import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { EnumSubscriptionModule } from '@apps/flying/shared';
import { JwtHandlerService } from '@libs/libraries/shared';
import { IUserAccessTokenPayload } from '@domains/user/shared';
import { IFlyingAccessRight, IFlyingAccessTokenData } from '@apps-v2/flying/shared';
import { firstValueFrom } from 'rxjs';

import { StatesService } from '../../../states/states.service';
import { AuthRequest } from '../../../../../requests/shared/auth/auth.request';
import { IRefreshTokenParams, ITokenResponse } from '../../../../../requests/shared/auth/auth.interface';
import { IAuthStorageService } from './access-token.interface';


@Injectable({
	providedIn: 'root'
})
export class AccessTokenService {
	private refreshPromise			: Promise<boolean> = null;
	private signoutPromise			: Promise<boolean> = null;
	private loginNavigationPromise	: Promise<boolean> = null;

	constructor(
		@Inject('IAuthStorageService')
		private authStorageService	: IAuthStorageService,
		private authRequest			: AuthRequest,
		private statesService		: StatesService,
		private router				: Router,
		private jwtHelper			: JwtHandlerService
	) {}


	reAuthenticate(): Promise<boolean> {
		// eslint-disable-next-line @typescript-eslint/no-misused-promises
		if (this.refreshPromise) return this.refreshPromise;

		const refreshToken: string = this.authStorageService.getRefreshToken();
		if (!refreshToken) return Promise.reject(false);
		const params: IRefreshTokenParams = { refreshToken };

		this.refreshPromise = firstValueFrom(this.authRequest.getRefreshTokenApi(params))
			.then((resData: ITokenResponse) => {
				// A refresh must not restore a session that was signed out or replaced.
				if (!resData?.token || this.authStorageService.getRefreshToken() !== refreshToken) throw new Error('Session changed during refresh.');
				this.authenticate(resData.token, refreshToken);
				return true;
			})
			.finally(() => {
				this.refreshPromise = null;
			});
		return this.refreshPromise;
	}

	signoutClient(): void {
		this.authStorageService.onSignOut();
		this.statesService.user.resetUser();
	}

	getBearerToken(): string {

		//0 - get the auth token
		const authToken: string = this.authStorageService.getAccessToken();

		//1 - create the JWT/Bearer token
		return `Bearer ${authToken}`;
	}

	getToken(): string {
		return this.authStorageService.getAccessToken();
	}

	isAuthenticated(): boolean {

		//0 - get the token
		const token: string = this.authStorageService.getAccessToken();
		if (!token) return false;

		//1 - did the token expire?
		const isTokenExpired: boolean = this.jwtHelper.isExpired(token);
		if (isTokenExpired) return false;

		//2 - set access rights
		const accessRights: IFlyingAccessRight[] = this.jwtHelper.extractPayload<IUserAccessTokenPayload<IFlyingAccessTokenData>>(token).data.accessRights;
		this.statesService.access.setAccessRights(accessRights);

		//3 - return that the user is authenticated
		return true;
	}

	authenticate(accessToken: string, refreshToken: string): void {

		//0 - save the refresh token
		this.authStorageService.setRefreshToken(refreshToken);

		//1 - set the access token
		this.setAccessToken(accessToken);
	}

	fullSignout(): Promise<boolean> {
		if (this.signoutPromise) return this.signoutPromise;

		// Start the server-side logout while the current access token is still
		// available, but never make the local transition wait for the network.
		// The client session is authoritative for leaving a protected screen.
		this.authRequest.logout().subscribe({
			error: () => undefined
		});

		this.signoutPromise = this.signoutAndRedirect(`You're logged out successfully.`).then(
			(navigated: boolean) => {
				this.signoutPromise = null;
				return navigated;
			},
			() => {
				this.signoutPromise = null;
				return false;
			}
		);

		return this.signoutPromise;
	}

	signoutAndRedirect(message: string): Promise<boolean> {
		this.signoutClient();
		if (this.loginNavigationPromise) return this.loginNavigationPromise;

		this.loginNavigationPromise = this.router.navigate(['/user/login'], {
			replaceUrl	: true,
			state		: { message }
		}).then(
			(navigated: boolean) => {
				this.loginNavigationPromise = null;
				return navigated;
			},
			() => {
				this.loginNavigationPromise = null;
				return false;
			}
		);

		return this.loginNavigationPromise;
	}


	getId(): string {
		return this.isAuthenticated()
			? this.jwtHelper.extractPayload<IUserAccessTokenPayload<IFlyingAccessTokenData>>(this.authStorageService.getAccessToken()).userId
			: null;
	}

	getUserEmail(): string {
		return this.isAuthenticated()
			? this.jwtHelper.extractPayload<IUserAccessTokenPayload<IFlyingAccessTokenData>>(this.authStorageService.getAccessToken()).profile.email
			: null;
	}

	getAccessRights(): IFlyingAccessRight[] {
		return this.isAuthenticated()
			? this.jwtHelper.extractPayload<IUserAccessTokenPayload<IFlyingAccessTokenData>>(this.authStorageService.getAccessToken()).data.accessRights
			: [];
	}

	hasModule(module: EnumSubscriptionModule): boolean {

		//0 - get all the access rights a the user has
		const accessRights: IFlyingAccessRight[] = this.getAccessRights();

		//1 - can we find the right for the subscription module?
		const foundRights : IFlyingAccessRight | null  = accessRights.find((accessRight: IFlyingAccessRight) => accessRight.module === module && new Date(accessRight.expires) >= new Date());
		if (!foundRights) return false;

		//2 - if the right was found the user has access
		return true;
	}

	setAccessToken(token: string): void {

		//0 - save the access token
		this.authStorageService.setAccessToken(token);

		//1 - get the access rights
		const accessRights: IFlyingAccessRight[] = this.isAuthenticated()
			? this.jwtHelper.extractPayload<IUserAccessTokenPayload<IFlyingAccessTokenData>>(token).data.accessRights
			: [];

		//2 - set the access rights
		this.statesService.access.setAccessRights(accessRights);
	}


	/**------------------------------------------------------
	 * Private / Public State Check
	 */
	isPrivateState(): boolean {
		return !this.isPublicState();
	}

	isPublicState(): boolean {
		return !this.authStorageService.getAccessToken() && !this.authStorageService.getRefreshToken();
	}
}
